import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, Form, Button, Table, Modal, Tabs, Tab,
  Spinner, Alert, Badge, InputGroup, ListGroup,
} from "react-bootstrap";
import { FaUtensils, FaPlus, FaTrash, FaBookOpen, FaCalendarAlt, FaSearch, FaEdit } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSearchParams } from "react-router-dom";
import { api } from "../../contexts/AuthContext";

const SIDEBAR_BLUE = "#60A5FA";

const MEAL_TYPES = [
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "snack", label: "Snack" },
  { value: "other", label: "Other" },
];

const FOOD_CATEGORIES = [
  { value: "", label: "All categories" },
  { value: "fruit", label: "Fruit" },
  { value: "vegetable", label: "Vegetable" },
  { value: "grain", label: "Grain & Cereal" },
  { value: "protein", label: "Protein" },
  { value: "dairy", label: "Dairy" },
  { value: "fat", label: "Fats & Oils" },
  { value: "beverage", label: "Beverage" },
  { value: "snack", label: "Snack" },
  { value: "other", label: "Other" },
];

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDate = (value) => {
  if (!value) return new Date();
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const displayDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return `${d}/${m}/${y}`;
};

const inputStyle = {
  backgroundColor: "#2d2d2d",
  border: `1px solid ${SIDEBAR_BLUE}`,
  color: "#fff",
};

const cardStyle = {
  backgroundColor: "#1e1e1e",
  border: `1px solid ${SIDEBAR_BLUE}`,
};

const NutritionPlanner = () => {
  const [searchParams] = useSearchParams();
  const focusDate = searchParams.get("date");

  const [tab, setTab] = useState("plans");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [plans, setPlans] = useState([]);
  const [supplementPlans, setSupplementPlans] = useState([]);
  const [foods, setFoods] = useState([]);
  const [recipes, setRecipes] = useState([]);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedSupplementPlan, setSelectedSupplementPlan] = useState(null);
  const [foodSearch, setFoodSearch] = useState("");
  const [foodCategory, setFoodCategory] = useState("");

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [showSupplementPlanModal, setShowSupplementPlanModal] = useState(false);
  const [editingSupplementPlanId, setEditingSupplementPlanId] = useState(null);
  const [showSupplementDoseModal, setShowSupplementDoseModal] = useState(false);
  const [showMealModal, setShowMealModal] = useState(false);
  const [showFoodModal, setShowFoodModal] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);

  const [planForm, setPlanForm] = useState({
    name: "",
    start_date: new Date(),
    end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    notes: "",
  });

  const [supplementPlanForm, setSupplementPlanForm] = useState({
    name: "",
    start_date: new Date(),
    end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    notes: "",
  });

  const [supplementDoseForm, setSupplementDoseForm] = useState({
    name: "",
    dosage: "",
    time: "08:00",
    notes: "",
  });

  const [mealForm, setMealForm] = useState({
    date: new Date(),
    time: "12:00",
    meal_type: "lunch",
    name: "",
    notes: "",
    food_id: "",
    quantity_g: 100,
  });

  const [foodForm, setFoodForm] = useState({
    name: "",
    category: "other",
    calories: 0,
    protein_g: 0,
    carbs_g: 0,
    fat_g: 0,
    fiber_g: 0,
    sugar_g: 0,
  });

  const [recipeForm, setRecipeForm] = useState({
    name: "",
    description: "",
    instructions: "",
    servings: 1,
    food_id: "",
    quantity_g: 100,
  });
  const [recipeDraftIngredients, setRecipeDraftIngredients] = useState([]);

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bootstrap = async () => {
    try {
      setLoading(true);
      setError(null);
      const [plansRes, foodsRes, recipesRes, supplementPlansRes] = await Promise.all([
        api.get("/api/nutrition-plans/"),
        api.get("/api/foods/"),
        api.get("/api/recipes/"),
        api.get("/api/supplement-plans/"),
      ]);
      const planList = plansRes.data.results || plansRes.data;
      const foodList = foodsRes.data.results || foodsRes.data;
      const recipeList = recipesRes.data.results || recipesRes.data;
      const supplementPlanList = supplementPlansRes.data.results || supplementPlansRes.data;
      setPlans(planList);
      setFoods(foodList);
      setRecipes(recipeList);
      setSupplementPlans(supplementPlanList);

      if (focusDate) {
        const match = planList.find(
          (plan) => plan.start_date <= focusDate && plan.end_date >= focusDate
        );
        if (match) {
          const detail = await api.get(`/api/nutrition-plans/${match.id}/`);
          setSelectedPlan(detail.data);
        }
        const suppMatch = supplementPlanList.find(
          (plan) => plan.start_date <= focusDate && plan.end_date >= focusDate
        );
        if (suppMatch) {
          const detail = await api.get(`/api/supplement-plans/${suppMatch.id}/`);
          setSelectedSupplementPlan(detail.data);
        }
      }
    } catch (err) {
      console.error(err);
      setError("Could not load nutrition data.");
    } finally {
      setLoading(false);
    }
  };

  const refreshFoods = async () => {
    const params = {};
    if (foodSearch.trim()) params.search = foodSearch.trim();
    if (foodCategory) params.category = foodCategory;
    const res = await api.get("/api/foods/", { params });
    setFoods(res.data.results || res.data);
  };

  useEffect(() => {
    if (tab === "foods") {
      refreshFoods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foodSearch, foodCategory, tab]);

  const openPlan = async (planId) => {
    const detail = await api.get(`/api/nutrition-plans/${planId}/`);
    setSelectedPlan(detail.data);
  };

  const resetPlanForm = () => {
    setEditingPlanId(null);
    setPlanForm({
      name: "",
      start_date: new Date(),
      end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      notes: "",
    });
  };

  const openCreatePlanModal = () => {
    resetPlanForm();
    setShowPlanModal(true);
  };

  const openEditPlanModal = (plan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name || "",
      start_date: parseDate(plan.start_date),
      end_date: parseDate(plan.end_date),
      notes: plan.notes || "",
    });
    setShowPlanModal(true);
  };

  const blockedDateIntervals = plans
    .filter((plan) => plan.id !== editingPlanId)
    .map((plan) => ({
      start: parseDate(plan.start_date),
      end: parseDate(plan.end_date),
    }));

  const rangesOverlap = (startA, endA, startB, endB) => startA <= endB && endA >= startB;

  const findPlanOverlap = (start, end) => {
    const startStr = formatDate(start);
    const endStr = formatDate(end);
    return plans.find(
      (plan) =>
        plan.id !== editingPlanId &&
        rangesOverlap(startStr, endStr, plan.start_date, plan.end_date)
    );
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    if (planForm.end_date < planForm.start_date) {
      setError("End date must be on or after start date.");
      return;
    }
    const conflict = findPlanOverlap(planForm.start_date, planForm.end_date);
    if (conflict) {
      setError(
        `Date range overlaps with "${conflict.name}" (${displayDate(conflict.start_date)} → ${displayDate(conflict.end_date)}).`
      );
      return;
    }
    const payload = {
      name: planForm.name.trim(),
      start_date: formatDate(planForm.start_date),
      end_date: formatDate(planForm.end_date),
      notes: planForm.notes,
    };
    try {
      setError(null);
      let saved;
      if (editingPlanId) {
        const res = await api.patch(`/api/nutrition-plans/${editingPlanId}/`, payload);
        saved = res.data;
        setPlans((prev) => prev.map((plan) => (plan.id === editingPlanId ? { ...plan, ...saved } : plan)));
        if (selectedPlan?.id === editingPlanId) {
          await openPlan(editingPlanId);
        }
      } else {
        const res = await api.post("/api/nutrition-plans/", payload);
        saved = res.data;
        setPlans((prev) => [saved, ...prev]);
        setSelectedPlan(saved);
      }
      setShowPlanModal(false);
      resetPlanForm();
      setTab("plans");
    } catch (err) {
      console.error(err);
      const apiMsg =
        err.response?.data?.start_date?.[0] ||
        err.response?.data?.end_date?.[0] ||
        err.response?.data?.non_field_errors?.[0];
      setError(apiMsg || "Could not save plan.");
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await api.delete(`/api/nutrition-plans/${planId}/`);
      setPlans((prev) => prev.filter((plan) => plan.id !== planId));
      if (selectedPlan?.id === planId) setSelectedPlan(null);
    } catch (err) {
      console.error(err);
      setError("Could not delete plan.");
    }
  };

  const handleAddMeal = async (e) => {
    e.preventDefault();
    if (!selectedPlan) return;
    const mealDate = formatDate(mealForm.date);
    if (mealDate < selectedPlan.start_date || mealDate > selectedPlan.end_date) {
      setError("Meal date must be inside the plan date range.");
      return;
    }
    try {
      setError(null);
      const mealRes = await api.post("/api/nutrition-meals/", {
        plan: selectedPlan.id,
        date: mealDate,
        time: mealForm.time.length === 5 ? `${mealForm.time}:00` : mealForm.time,
        meal_type: mealForm.meal_type,
        name: mealForm.name,
        notes: mealForm.notes,
      });

      if (mealForm.food_id) {
        await api.post("/api/nutrition-meal-items/", {
          meal: mealRes.data.id,
          food: Number(mealForm.food_id),
          quantity_g: Number(mealForm.quantity_g) || 100,
        });
      }

      await openPlan(selectedPlan.id);
      setShowMealModal(false);
      setMealForm({
        date: mealForm.date,
        time: "12:00",
        meal_type: "lunch",
        name: "",
        notes: "",
        food_id: "",
        quantity_g: 100,
      });
    } catch (err) {
      console.error(err);
      setError("Could not add meal.");
    }
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await api.delete(`/api/nutrition-meals/${mealId}/`);
      await openPlan(selectedPlan.id);
    } catch (err) {
      console.error(err);
      setError("Could not delete meal.");
    }
  };

  const handleCreateFood = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      await api.post("/api/foods/", foodForm);
      setShowFoodModal(false);
      setFoodForm({
        name: "",
        category: "other",
        calories: 0,
        protein_g: 0,
        carbs_g: 0,
        fat_g: 0,
        fiber_g: 0,
        sugar_g: 0,
      });
      await refreshFoods();
    } catch (err) {
      console.error(err);
      setError("Could not create food. Name may already exist.");
    }
  };

  const handleCreateRecipe = async (e) => {
    e.preventDefault();
    if (!recipeForm.name.trim() || recipeDraftIngredients.length === 0) {
      setError("Recipe needs a name and at least one ingredient.");
      return;
    }
    try {
      setError(null);
      const recipeRes = await api.post("/api/recipes/", {
        name: recipeForm.name.trim(),
        description: recipeForm.description,
        instructions: recipeForm.instructions,
        servings: Number(recipeForm.servings) || 1,
      });
      await Promise.all(
        recipeDraftIngredients.map((item) =>
          api.post("/api/recipe-ingredients/", {
            recipe: recipeRes.data.id,
            food: item.food_id,
            quantity_g: item.quantity_g,
          })
        )
      );
      const recipesRes = await api.get("/api/recipes/");
      setRecipes(recipesRes.data.results || recipesRes.data);
      setShowRecipeModal(false);
      setRecipeForm({
        name: "",
        description: "",
        instructions: "",
        servings: 1,
        food_id: "",
        quantity_g: 100,
      });
      setRecipeDraftIngredients([]);
    } catch (err) {
      console.error(err);
      setError("Could not create recipe.");
    }
  };

  const addDraftIngredient = () => {
    if (!recipeForm.food_id) return;
    const food = foods.find((item) => item.id === Number(recipeForm.food_id));
    if (!food) return;
    setRecipeDraftIngredients((prev) => [
      ...prev,
      {
        food_id: food.id,
        food_name: food.name,
        quantity_g: Number(recipeForm.quantity_g) || 100,
      },
    ]);
  };

  const setRangePreset = (days) => {
    const start = planForm.start_date ? new Date(planForm.start_date) : new Date();
    const end = new Date(start);
    end.setDate(start.getDate() + days - 1);
    setPlanForm((prev) => ({ ...prev, start_date: start, end_date: end }));
  };

  const setSupplementRangePreset = (days) => {
    const start = supplementPlanForm.start_date ? new Date(supplementPlanForm.start_date) : new Date();
    const end = new Date(start);
    end.setDate(start.getDate() + days - 1);
    setSupplementPlanForm((prev) => ({ ...prev, start_date: start, end_date: end }));
  };

  const openSupplementPlan = async (planId) => {
    const detail = await api.get(`/api/supplement-plans/${planId}/`);
    setSelectedSupplementPlan(detail.data);
  };

  const resetSupplementPlanForm = () => {
    setEditingSupplementPlanId(null);
    setSupplementPlanForm({
      name: "",
      start_date: new Date(),
      end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      notes: "",
    });
  };

  const openCreateSupplementPlanModal = () => {
    resetSupplementPlanForm();
    setShowSupplementPlanModal(true);
  };

  const openEditSupplementPlanModal = (plan) => {
    setEditingSupplementPlanId(plan.id);
    setSupplementPlanForm({
      name: plan.name || "",
      start_date: parseDate(plan.start_date),
      end_date: parseDate(plan.end_date),
      notes: plan.notes || "",
    });
    setShowSupplementPlanModal(true);
  };

  const blockedSupplementDateIntervals = supplementPlans
    .filter((plan) => plan.id !== editingSupplementPlanId)
    .map((plan) => ({
      start: parseDate(plan.start_date),
      end: parseDate(plan.end_date),
    }));

  const findSupplementPlanOverlap = (start, end) => {
    const startStr = formatDate(start);
    const endStr = formatDate(end);
    return supplementPlans.find(
      (plan) =>
        plan.id !== editingSupplementPlanId &&
        rangesOverlap(startStr, endStr, plan.start_date, plan.end_date)
    );
  };

  const handleSaveSupplementPlan = async (e) => {
    e.preventDefault();
    if (supplementPlanForm.end_date < supplementPlanForm.start_date) {
      setError("End date must be on or after start date.");
      return;
    }
    const conflict = findSupplementPlanOverlap(
      supplementPlanForm.start_date,
      supplementPlanForm.end_date
    );
    if (conflict) {
      setError(
        `Supplement plan overlaps with "${conflict.name}" (${displayDate(conflict.start_date)} → ${displayDate(conflict.end_date)}).`
      );
      return;
    }
    const payload = {
      name: supplementPlanForm.name.trim(),
      start_date: formatDate(supplementPlanForm.start_date),
      end_date: formatDate(supplementPlanForm.end_date),
      notes: supplementPlanForm.notes,
    };
    try {
      setError(null);
      if (editingSupplementPlanId) {
        const res = await api.patch(`/api/supplement-plans/${editingSupplementPlanId}/`, payload);
        setSupplementPlans((prev) =>
          prev.map((plan) => (plan.id === editingSupplementPlanId ? { ...plan, ...res.data } : plan))
        );
        if (selectedSupplementPlan?.id === editingSupplementPlanId) {
          await openSupplementPlan(editingSupplementPlanId);
        }
      } else {
        const res = await api.post("/api/supplement-plans/", payload);
        setSupplementPlans((prev) => [res.data, ...prev]);
        setSelectedSupplementPlan(res.data);
      }
      setShowSupplementPlanModal(false);
      resetSupplementPlanForm();
      setTab("supplements");
    } catch (err) {
      console.error(err);
      const apiMsg =
        err.response?.data?.start_date?.[0] ||
        err.response?.data?.end_date?.[0] ||
        err.response?.data?.non_field_errors?.[0];
      setError(apiMsg || "Could not save supplement plan.");
    }
  };

  const handleDeleteSupplementPlan = async (planId) => {
    try {
      await api.delete(`/api/supplement-plans/${planId}/`);
      setSupplementPlans((prev) => prev.filter((plan) => plan.id !== planId));
      if (selectedSupplementPlan?.id === planId) setSelectedSupplementPlan(null);
    } catch (err) {
      console.error(err);
      setError("Could not delete supplement plan.");
    }
  };

  const handleAddSupplementDose = async (e) => {
    e.preventDefault();
    if (!selectedSupplementPlan) return;
    if (!supplementDoseForm.name.trim()) {
      setError("Supplement name is required.");
      return;
    }
    try {
      setError(null);
      await api.post("/api/supplement-doses/bulk-create/", {
        plan: selectedSupplementPlan.id,
        name: supplementDoseForm.name.trim(),
        dosage: supplementDoseForm.dosage.trim(),
        time: supplementDoseForm.time,
        notes: supplementDoseForm.notes,
      });
      await openSupplementPlan(selectedSupplementPlan.id);
      setShowSupplementDoseModal(false);
      setSupplementDoseForm({ name: "", dosage: "", time: "08:00", notes: "" });
    } catch (err) {
      console.error(err);
      setError("Could not add supplement.");
    }
  };

  const handleDeleteSupplementSchedule = async (schedule) => {
    if (!selectedSupplementPlan) return;
    try {
      await api.post("/api/supplement-doses/bulk-delete/", {
        plan: selectedSupplementPlan.id,
        name: schedule.name,
        dosage: schedule.dosage || "",
        time: (schedule.time || "").length === 5 ? `${schedule.time}:00` : schedule.time,
      });
      await openSupplementPlan(selectedSupplementPlan.id);
    } catch (err) {
      console.error(err);
      setError("Could not remove supplement.");
    }
  };

  const supplementSchedules = (() => {
    const doses = selectedSupplementPlan?.doses || [];
    const map = new Map();
    doses.forEach((dose) => {
      const key = `${dose.name}|${dose.time}|${dose.dosage || ""}`;
      if (!map.has(key)) {
        map.set(key, {
          name: dose.name,
          time: dose.time,
          dosage: dose.dosage || "",
          notes: dose.notes || "",
          days: 0,
        });
      }
      map.get(key).days += 1;
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.time).localeCompare(String(b.time)) || a.name.localeCompare(b.name)
    );
  })();

  if (loading) {
    return (
      <Container fluid className="text-center py-5">
        <Spinner animation="border" style={{ color: SIDEBAR_BLUE }} />
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div className="d-flex align-items-center">
          <FaUtensils className="me-2" style={{ color: SIDEBAR_BLUE, fontSize: "1.5rem" }} />
          <h2 className="mb-0" style={{ color: "#fff" }}>Nutrition Planner</h2>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        activeKey={tab}
        onSelect={(key) => setTab(key || "plans")}
        className="mb-4 nutrition-tabs"
      >
        <Tab eventKey="plans" title="Plans">
          <Row>
            <Col lg={4} className="mb-4">
              <Card style={cardStyle}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 style={{ color: "#fff", margin: 0 }}>Your plans</h5>
                    <Button
                      size="sm"
                      onClick={openCreatePlanModal}
                      style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}
                    >
                      <FaPlus className="me-1" /> New plan
                    </Button>
                  </div>

                  {plans.length === 0 ? (
                    <p style={{ color: "#9ca3af" }}>No plans yet. Create one for any date range.</p>
                  ) : (
                    <ListGroup variant="flush">
                      {plans.map((plan) => (
                        <ListGroup.Item
                          key={plan.id}
                          action
                          onClick={() => openPlan(plan.id)}
                          active={selectedPlan?.id === plan.id}
                          style={{
                            backgroundColor: selectedPlan?.id === plan.id ? "#1e3a5f" : "#2d2d2d",
                            border: `1px solid ${SIDEBAR_BLUE}`,
                            color: "#fff",
                            marginBottom: 8,
                            borderRadius: 4,
                          }}
                        >
                          <div className="d-flex justify-content-between">
                            <strong>{plan.name}</strong>
                            <Button
                              variant="link"
                              size="sm"
                              style={{ color: "#F87171" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePlan(plan.id);
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                          <small style={{ color: "#9ca3af" }}>
                            {displayDate(plan.start_date)} → {displayDate(plan.end_date)}
                          </small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={8}>
              <Card style={cardStyle}>
                <Card.Body>
                  {!selectedPlan ? (
                    <p style={{ color: "#9ca3af" }}>Select a plan to manage meals.</p>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                        <div>
                          <h4 style={{ color: "#fff" }}>{selectedPlan.name}</h4>
                          <p style={{ color: "#d1d5db", marginBottom: 0 }}>
                            <FaCalendarAlt className="me-2" />
                            {displayDate(selectedPlan.start_date)} → {displayDate(selectedPlan.end_date)}
                          </p>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-info"
                            onClick={() => openEditPlanModal(selectedPlan)}
                          >
                            <FaEdit className="me-1" /> Edit plan
                          </Button>
                          <Button
                            onClick={() => {
                              setMealForm((prev) => ({
                                ...prev,
                                date: parseDate(selectedPlan.start_date),
                              }));
                              setShowMealModal(true);
                            }}
                            style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}
                          >
                            <FaPlus className="me-1" /> Add meal
                          </Button>
                        </div>
                      </div>

                      {(selectedPlan.meals || []).length === 0 ? (
                        <p style={{ color: "#9ca3af" }}>No meals in this plan yet.</p>
                      ) : (
                        <Table responsive hover variant="dark" style={{ color: "#fff" }}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Time</th>
                              <th>Type</th>
                              <th>Name</th>
                              <th>Items</th>
                              <th>kcal</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedPlan.meals.map((meal) => (
                              <tr key={meal.id}>
                                <td>{displayDate(meal.date)}</td>
                                <td>{(meal.time || "").slice(0, 5)}</td>
                                <td>
                                  <Badge bg="secondary">{meal.meal_type}</Badge>
                                </td>
                                <td>{meal.name || "—"}</td>
                                <td>
                                  {(meal.items || []).map((item) => (
                                    <div key={item.id} style={{ fontSize: "0.85rem", color: "#d1d5db" }}>
                                      {item.food_name
                                        ? `${item.quantity_g}g ${item.food_name}`
                                        : item.recipe_name}
                                    </div>
                                  ))}
                                </td>
                                <td>{meal.calories ?? 0}</td>
                                <td>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    style={{ color: "#F87171" }}
                                    onClick={() => handleDeleteMeal(meal.id)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="supplements" title="Supplements">
          <Row>
            <Col lg={4} className="mb-4">
              <Card style={cardStyle}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 style={{ color: "#fff", margin: 0 }}>
                      Your plans
                    </h5>
                    <Button
                      size="sm"
                      onClick={openCreateSupplementPlanModal}
                      style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}
                    >
                      <FaPlus className="me-1" /> New plan
                    </Button>
                  </div>

                  {supplementPlans.length === 0 ? (
                    <p style={{ color: "#9ca3af" }}>
                      No plans yet. Create a date range, then add daily supplements.
                    </p>
                  ) : (
                    <ListGroup variant="flush">
                      {supplementPlans.map((plan) => (
                        <ListGroup.Item
                          key={plan.id}
                          action
                          onClick={() => openSupplementPlan(plan.id)}
                          active={selectedSupplementPlan?.id === plan.id}
                          style={{
                            backgroundColor: selectedSupplementPlan?.id === plan.id ? "#1e3a5f" : "#2d2d2d",
                            border: `1px solid ${SIDEBAR_BLUE}`,
                            color: "#fff",
                            marginBottom: 8,
                            borderRadius: 4,
                          }}
                        >
                          <div className="d-flex justify-content-between">
                            <strong>{plan.name}</strong>
                            <Button
                              variant="link"
                              size="sm"
                              style={{ color: "#F87171" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSupplementPlan(plan.id);
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                          <small style={{ color: "#9ca3af" }}>
                            {displayDate(plan.start_date)} → {displayDate(plan.end_date)}
                          </small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={8}>
              <Card style={cardStyle}>
                <Card.Body>
                  {!selectedSupplementPlan ? (
                    <p style={{ color: "#9ca3af" }}>Select a plan to manage supplements.</p>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                        <div>
                          <h4 style={{ color: "#fff" }}>{selectedSupplementPlan.name}</h4>
                          <p style={{ color: "#d1d5db", marginBottom: 0 }}>
                            <FaCalendarAlt className="me-2" />
                            {displayDate(selectedSupplementPlan.start_date)} → {displayDate(selectedSupplementPlan.end_date)}
                          </p>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-info"
                            onClick={() => openEditSupplementPlanModal(selectedSupplementPlan)}
                          >
                            <FaEdit className="me-1" /> Edit plan
                          </Button>
                          <Button
                            onClick={() => setShowSupplementDoseModal(true)}
                            style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}
                          >
                            <FaPlus className="me-1" /> Add supplement
                          </Button>
                        </div>
                      </div>

                      <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                        Supplements added here are scheduled every day in this date range and show up in Day Builder.
                      </p>

                      {supplementSchedules.length === 0 ? (
                        <p style={{ color: "#9ca3af" }}>No supplements in this plan yet.</p>
                      ) : (
                        <Table responsive hover variant="dark" style={{ color: "#fff" }}>
                          <thead>
                            <tr>
                              <th>Time</th>
                              <th>Name</th>
                              <th>Dosage</th>
                              <th>Days</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {supplementSchedules.map((schedule) => (
                              <tr key={`${schedule.name}-${schedule.time}-${schedule.dosage}`}>
                                <td>{(schedule.time || "").slice(0, 5)}</td>
                                <td>{schedule.name}</td>
                                <td>{schedule.dosage || "—"}</td>
                                <td>{schedule.days}</td>
                                <td>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    style={{ color: "#F87171" }}
                                    onClick={() => handleDeleteSupplementSchedule(schedule)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="foods" title="Foods">
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 style={{ color: "#fff", margin: 0 }}>Food database</h5>
                <Button
                  size="sm"
                  onClick={() => setShowFoodModal(true)}
                  style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}
                >
                  <FaPlus className="me-1" /> Add food
                </Button>
              </div>
              <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
                Values are per 100g. Search the seeded common foods or add your own.
              </p>
              <Row className="mb-3 g-2">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text style={{ backgroundColor: "#2d2d2d", borderColor: SIDEBAR_BLUE, color: "#fff" }}>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search foods..."
                      value={foodSearch}
                      onChange={(e) => setFoodSearch(e.target.value)}
                      style={inputStyle}
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <Form.Select
                    value={foodCategory}
                    onChange={(e) => setFoodCategory(e.target.value)}
                    style={inputStyle}
                  >
                    {FOOD_CATEGORIES.map((cat) => (
                      <option key={cat.value || "all"} value={cat.value}>{cat.label}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              <Table responsive hover variant="dark">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>kcal</th>
                    <th>P</th>
                    <th>C</th>
                    <th>F</th>
                    <th>Fiber</th>
                  </tr>
                </thead>
                <tbody>
                  {foods.map((food) => (
                    <tr key={food.id}>
                      <td>{food.name}</td>
                      <td>{food.category}</td>
                      <td>{food.calories}</td>
                      <td>{food.protein_g}g</td>
                      <td>{food.carbs_g}g</td>
                      <td>{food.fat_g}g</td>
                      <td>{food.fiber_g}g</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="recipes" title="Recipes">
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 style={{ color: "#fff", margin: 0 }}>
                  <FaBookOpen className="me-2" />
                  Recipes
                </h5>
                <Button
                  size="sm"
                  onClick={() => setShowRecipeModal(true)}
                  style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}
                >
                  <FaPlus className="me-1" /> New recipe
                </Button>
              </div>

              {recipes.length === 0 ? (
                <p style={{ color: "#9ca3af" }}>No recipes yet.</p>
              ) : (
                <Row>
                  {recipes.map((recipe) => (
                    <Col md={6} key={recipe.id} className="mb-3">
                      <Card style={{ backgroundColor: "#2d2d2d", border: `1px solid ${SIDEBAR_BLUE}` }}>
                        <Card.Body>
                          <h6 style={{ color: "#fff" }}>{recipe.name}</h6>
                          <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                            {recipe.servings} serving(s) · ~{recipe.calories} kcal/serving
                          </p>
                          <ul style={{ color: "#d1d5db", fontSize: "0.85rem", marginBottom: 0 }}>
                            {(recipe.ingredients || []).map((ing) => (
                              <li key={ing.id}>{ing.quantity_g}g {ing.food_name}</li>
                            ))}
                          </ul>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Create / edit plan modal */}
      <Modal
        show={showPlanModal}
        onHide={() => {
          setShowPlanModal(false);
          resetPlanForm();
        }}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton closeVariant="white" style={{ borderColor: SIDEBAR_BLUE }}>
          <Modal.Title>{editingPlanId ? "Edit nutrition plan" : "New nutrition plan"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSavePlan}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={planForm.name}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. April cut, 15-day reset"
                style={inputStyle}
              />
            </Form.Group>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button size="sm" variant="outline-info" type="button" onClick={() => setRangePreset(7)}>7 days</Button>
              <Button size="sm" variant="outline-info" type="button" onClick={() => setRangePreset(15)}>15 days</Button>
              <Button size="sm" variant="outline-info" type="button" onClick={() => setRangePreset(30)}>30 days</Button>
            </div>
            {blockedDateIntervals.length > 0 && (
              <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                Dates already covered by other plans are disabled in the calendars.
              </p>
            )}
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Start</Form.Label>
                  <DatePicker
                    selected={planForm.start_date}
                    onChange={(date) => setPlanForm((prev) => ({
                      ...prev,
                      start_date: date,
                      end_date: prev.end_date && date && prev.end_date < date ? date : prev.end_date,
                    }))}
                    dateFormat="dd/MM/yyyy"
                    excludeDateIntervals={blockedDateIntervals}
                    customInput={<Form.Control style={inputStyle} />}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>End</Form.Label>
                  <DatePicker
                    selected={planForm.end_date}
                    onChange={(date) => setPlanForm((prev) => ({ ...prev, end_date: date }))}
                    dateFormat="dd/MM/yyyy"
                    minDate={planForm.start_date}
                    excludeDateIntervals={blockedDateIntervals}
                    customInput={<Form.Control style={inputStyle} />}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={planForm.notes}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, notes: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderColor: SIDEBAR_BLUE }}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPlanModal(false);
                resetPlanForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}>
              {editingPlanId ? "Save changes" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add meal modal */}
      <Modal show={showMealModal} onHide={() => setShowMealModal(false)} centered contentClassName="bg-dark text-white">
        <Modal.Header closeButton closeVariant="white" style={{ borderColor: SIDEBAR_BLUE }}>
          <Modal.Title>Add meal</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddMeal}>
          <Modal.Body>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <DatePicker
                    selected={mealForm.date}
                    onChange={(date) => setMealForm((prev) => ({ ...prev, date }))}
                    dateFormat="dd/MM/yyyy"
                    customInput={<Form.Control style={inputStyle} />}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Time</Form.Label>
                  <Form.Control
                    type="time"
                    required
                    value={mealForm.time}
                    onChange={(e) => setMealForm((prev) => ({ ...prev, time: e.target.value }))}
                    style={inputStyle}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Meal type</Form.Label>
              <Form.Select
                value={mealForm.meal_type}
                onChange={(e) => setMealForm((prev) => ({ ...prev, meal_type: e.target.value }))}
                style={inputStyle}
              >
                {MEAL_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Label (optional)</Form.Label>
              <Form.Control
                value={mealForm.name}
                onChange={(e) => setMealForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Post-workout"
                style={inputStyle}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Food</Form.Label>
              <Form.Select
                value={mealForm.food_id}
                onChange={(e) => setMealForm((prev) => ({ ...prev, food_id: e.target.value }))}
                style={inputStyle}
              >
                <option value="">Select food...</option>
                {foods.map((food) => (
                  <option key={food.id} value={food.id}>{food.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Quantity (g)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="1"
                value={mealForm.quantity_g}
                onChange={(e) => setMealForm((prev) => ({ ...prev, quantity_g: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderColor: SIDEBAR_BLUE }}>
            <Button variant="secondary" onClick={() => setShowMealModal(false)}>Cancel</Button>
            <Button type="submit" style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}>Add meal</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add food modal */}
      <Modal show={showFoodModal} onHide={() => setShowFoodModal(false)} centered contentClassName="bg-dark text-white">
        <Modal.Header closeButton closeVariant="white" style={{ borderColor: SIDEBAR_BLUE }}>
          <Modal.Title>Add food (per 100g)</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateFood}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={foodForm.name}
                onChange={(e) => setFoodForm((prev) => ({ ...prev, name: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={foodForm.category}
                onChange={(e) => setFoodForm((prev) => ({ ...prev, category: e.target.value }))}
                style={inputStyle}
              >
                {FOOD_CATEGORIES.filter((c) => c.value).map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Row>
              {["calories", "protein_g", "carbs_g", "fat_g", "fiber_g", "sugar_g"].map((field) => (
                <Col xs={6} key={field} className="mb-3">
                  <Form.Label>{field.replace("_g", " (g)").replace("calories", "Calories")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0"
                    value={foodForm[field]}
                    onChange={(e) => setFoodForm((prev) => ({ ...prev, [field]: e.target.value }))}
                    style={inputStyle}
                  />
                </Col>
              ))}
            </Row>
          </Modal.Body>
          <Modal.Footer style={{ borderColor: SIDEBAR_BLUE }}>
            <Button variant="secondary" onClick={() => setShowFoodModal(false)}>Cancel</Button>
            <Button type="submit" style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}>Save food</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Recipe modal */}
      <Modal show={showRecipeModal} onHide={() => setShowRecipeModal(false)} centered size="lg" contentClassName="bg-dark text-white">
        <Modal.Header closeButton closeVariant="white" style={{ borderColor: SIDEBAR_BLUE }}>
          <Modal.Title>New recipe</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateRecipe}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={recipeForm.name}
                onChange={(e) => setRecipeForm((prev) => ({ ...prev, name: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    value={recipeForm.description}
                    onChange={(e) => setRecipeForm((prev) => ({ ...prev, description: e.target.value }))}
                    style={inputStyle}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Servings</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={recipeForm.servings}
                    onChange={(e) => setRecipeForm((prev) => ({ ...prev, servings: e.target.value }))}
                    style={inputStyle}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={recipeForm.instructions}
                onChange={(e) => setRecipeForm((prev) => ({ ...prev, instructions: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>

            <h6 style={{ color: "#fff" }}>Ingredients</h6>
            <Row className="g-2 mb-2">
              <Col md={7}>
                <Form.Select
                  value={recipeForm.food_id}
                  onChange={(e) => setRecipeForm((prev) => ({ ...prev, food_id: e.target.value }))}
                  style={inputStyle}
                >
                  <option value="">Select food...</option>
                  {foods.map((food) => (
                    <option key={food.id} value={food.id}>{food.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Control
                  type="number"
                  min="1"
                  value={recipeForm.quantity_g}
                  onChange={(e) => setRecipeForm((prev) => ({ ...prev, quantity_g: e.target.value }))}
                  style={inputStyle}
                  placeholder="grams"
                />
              </Col>
              <Col md={2}>
                <Button type="button" onClick={addDraftIngredient} style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE, width: "100%" }}>
                  Add
                </Button>
              </Col>
            </Row>
            <ListGroup>
              {recipeDraftIngredients.map((item, index) => (
                <ListGroup.Item
                  key={`${item.food_id}-${index}`}
                  className="d-flex justify-content-between"
                  style={{ backgroundColor: "#2d2d2d", color: "#fff", borderColor: SIDEBAR_BLUE }}
                >
                  <span>{item.quantity_g}g {item.food_name}</span>
                  <Button
                    variant="link"
                    size="sm"
                    style={{ color: "#F87171" }}
                    onClick={() => setRecipeDraftIngredients((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <FaTrash />
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Modal.Body>
          <Modal.Footer style={{ borderColor: SIDEBAR_BLUE }}>
            <Button variant="secondary" onClick={() => setShowRecipeModal(false)}>Cancel</Button>
            <Button type="submit" style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}>Save recipe</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Create / edit supplement plan modal */}
      <Modal
        show={showSupplementPlanModal}
        onHide={() => {
          setShowSupplementPlanModal(false);
          resetSupplementPlanForm();
        }}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton closeVariant="white" style={{ borderColor: SIDEBAR_BLUE }}>
          <Modal.Title>
            {editingSupplementPlanId ? "Edit supplement plan" : "New supplement plan"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveSupplementPlan}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={supplementPlanForm.name}
                onChange={(e) => setSupplementPlanForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. July vitamins"
                style={inputStyle}
              />
            </Form.Group>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button size="sm" variant="outline-info" type="button" onClick={() => setSupplementRangePreset(7)}>7 days</Button>
              <Button size="sm" variant="outline-info" type="button" onClick={() => setSupplementRangePreset(15)}>15 days</Button>
              <Button size="sm" variant="outline-info" type="button" onClick={() => setSupplementRangePreset(30)}>30 days</Button>
            </div>
            {blockedSupplementDateIntervals.length > 0 && (
              <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                Dates already covered by other supplement plans are disabled.
              </p>
            )}
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Start</Form.Label>
                  <DatePicker
                    selected={supplementPlanForm.start_date}
                    onChange={(date) => setSupplementPlanForm((prev) => ({
                      ...prev,
                      start_date: date,
                      end_date: prev.end_date && date && prev.end_date < date ? date : prev.end_date,
                    }))}
                    dateFormat="dd/MM/yyyy"
                    excludeDateIntervals={blockedSupplementDateIntervals}
                    customInput={<Form.Control style={inputStyle} />}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>End</Form.Label>
                  <DatePicker
                    selected={supplementPlanForm.end_date}
                    onChange={(date) => setSupplementPlanForm((prev) => ({ ...prev, end_date: date }))}
                    dateFormat="dd/MM/yyyy"
                    minDate={supplementPlanForm.start_date}
                    excludeDateIntervals={blockedSupplementDateIntervals}
                    customInput={<Form.Control style={inputStyle} />}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={supplementPlanForm.notes}
                onChange={(e) => setSupplementPlanForm((prev) => ({ ...prev, notes: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderColor: SIDEBAR_BLUE }}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSupplementPlanModal(false);
                resetSupplementPlanForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}>
              {editingSupplementPlanId ? "Save changes" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add supplement dose modal */}
      <Modal
        show={showSupplementDoseModal}
        onHide={() => setShowSupplementDoseModal(false)}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton closeVariant="white" style={{ borderColor: SIDEBAR_BLUE }}>
          <Modal.Title>Add supplement</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddSupplementDose}>
          <Modal.Body>
            <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
              This will be added for every day in the selected plan’s date range.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={supplementDoseForm.name}
                onChange={(e) => setSupplementDoseForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Vitamin D, protein powder, Magnesium"
                style={inputStyle}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dosage</Form.Label>
              <Form.Control
                value={supplementDoseForm.dosage}
                onChange={(e) => setSupplementDoseForm((prev) => ({ ...prev, dosage: e.target.value }))}
                placeholder="e.g. 2000 IU, 1 capsule"
                style={inputStyle}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Time</Form.Label>
              <Form.Control
                type="time"
                required
                value={supplementDoseForm.time}
                onChange={(e) => setSupplementDoseForm((prev) => ({ ...prev, time: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={supplementDoseForm.notes}
                onChange={(e) => setSupplementDoseForm((prev) => ({ ...prev, notes: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderColor: SIDEBAR_BLUE }}>
            <Button variant="secondary" onClick={() => setShowSupplementDoseModal(false)}>Cancel</Button>
            <Button type="submit" style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}>
              Add to all days
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default NutritionPlanner;
